/* eslint-disable max-len */
import React, {useState, useEffect} from 'react'
import {Box, Grid, Link, Typography} from '@mui/material'
import 'features/HomePage/TrackShipments/index.scss'
import {useTheme} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import clipboardAlertIcon from 'assets/images/shipment-status-icons/clipboard-alert-icon.svg'
import clipboardCheckIcon from 'assets/images/shipment-status-icons/clipboard-check-icon.svg'
import flightCheckIcon from 'assets/images/shipment-status-icons/flight-check-icon.svg'
import flightCogIcon from 'assets/images/shipment-status-icons/flight-cog-icon.svg'
import flightShieldIcon from 'assets/images/shipment-status-icons/flight-shield-icon.svg'

interface ShipmentStatusProps {
    currentStatus: Record<string, string | null>
    routingInfo: Record<string, string | null>
    unitInfo: Record<string, string | null>
    exceptionCarrierInfo?: Record<string, string>
    awbInfo?: Record<string, string>
}

const ShipmentStatus: React.FC<ShipmentStatusProps> = ({
    currentStatus,
    routingInfo,
    unitInfo,
    exceptionCarrierInfo = {},
    awbInfo = {}
}) => {
    const theme = useTheme()
    const belowSm = useMediaQuery(theme.breakpoints.down('sm'))
    const isExceptionCarrier = Object.keys(exceptionCarrierInfo).length > 0

    const [statusColorAndIcon, setStatusColorAndIcon] = useState<Record<string, any>>({
        textColor: 'black-text-color',
        iconBgColor: 'black-bg-color',
        icon: <img src={clipboardCheckIcon} alt='icon' />
    })

    // Setting the values of the text color and icon background color
    useEffect((): void => {
        if (!currentStatus) return

        const {group: statusGroup} = currentStatus

        switch (statusGroup) {
            case 'document':
                setStatusColorAndIcon((prev: Record<string, any>) => ({
                    ...prev,
                    textColor: 'black-text-color',
                    iconBgColor: 'black-bg-color',
                    icon: <img src={clipboardCheckIcon} alt='icon' />
                }))
                break
            case 'flight':
                setStatusColorAndIcon((prev: Record<string, any>) => ({
                    ...prev,
                    textColor: 'primary-text-color',
                    iconBgColor: 'primary-bg-color',
                    icon: <img src={flightCheckIcon} alt='icon' />
                }))
                break
            case 'shipment':
                setStatusColorAndIcon((prev: Record<string, any>) => ({
                    ...prev,
                    textColor: 'primary-text-color',
                    iconBgColor: 'primary-bg-color',
                    icon: <img src={flightCogIcon} alt='icon' />
                }))
                break
            case 'customs':
                setStatusColorAndIcon((prev: Record<string, any>) => ({
                    ...prev,
                    textColor: 'primary-text-color',
                    iconBgColor: 'primary-bg-color',
                    icon: <img src={flightShieldIcon} alt='icon' />
                }))
                break
            case 'discrepancy':
                setStatusColorAndIcon((prev: Record<string, any>) => ({
                    ...prev,
                    textColor: 'danger-text-color',
                    iconBgColor: 'danger-bg-color',
                    icon: <img src={clipboardAlertIcon} alt='icon' />
                }))
                break
            case 'delivery':
                setStatusColorAndIcon((prev: Record<string, any>) => ({
                    ...prev,
                    textColor: 'success-text-color',
                    iconBgColor: 'success-bg-color',
                    icon: <img src={clipboardCheckIcon} alt='icon' />
                }))
                break
        }
    }, [currentStatus])

    const getStatus = (status: Record<string, string | null>) => {
        if (status?.description && status?.description?.length > 0) return status?.description
        else return status?.status
    }

    const handleExceptionCarrierStatusLink = (carrierInfo: Record<string, string>, awbInfo: Record<string, string>) => {
        switch (carrierInfo.carrier || carrierInfo.airlineCode) {
            case 'AF-KL':
            case 'AF':
            case 'KL':
            case '074':
            case '057':
            case '2C':
            case '003':
                return `${carrierInfo.websiteLink}${awbInfo.airlineCode}-${awbInfo.awbNumber}`
            default:
                return undefined
        }
    }

    return (
        <Box>
            <Grid container justifyContent='center'>
                <Grid item xs={12} sm={10} md={10} lg={8}>
                    <Grid container className='status-container'>
                        {/* Status Title */}
                        {isExceptionCarrier ? (
                            <Grid item xs={12} className='centered'>
                                <Link
                                    target='_blank'
                                    href={handleExceptionCarrierStatusLink(exceptionCarrierInfo, awbInfo)}
                                    className='status-title status-link-text'
                                >
                                    Check for status
                                </Link>
                            </Grid>
                        ) : (
                            <Grid item xs={12} className={`status-title centered ${statusColorAndIcon.textColor}`}>
                                {getStatus(currentStatus) ?? '- -'}
                            </Grid>
                        )}
                        {/* Airport and Flight Dashed Border */}
                        <Grid item xs={12}>
                            <Grid container className='status-airport'>
                                <Grid
                                    item
                                    xs={2}
                                    className={`status-airport-code ${statusColorAndIcon.textColor}`}
                                    textAlign='right'
                                >
                                    {routingInfo?.originStation ?? '- -'}
                                </Grid>
                                <Grid item xs={8}>
                                    <Box className='status-symbol'>
                                        <Box className='status-border-pipe1' />
                                        <Box className='status-dashed-border' />
                                        <Box className='status-border-pipe2' />
                                        {!isExceptionCarrier && (
                                            <Box className='icon-bg-container'>
                                                <Box
                                                    className={`icon-container centered ${statusColorAndIcon.iconBgColor}`}
                                                >
                                                    <Box className='status-icon centered'>
                                                        {statusColorAndIcon.icon}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid
                                    item
                                    xs={2}
                                    className={`status-airport-code ${statusColorAndIcon.textColor}`}
                                    textAlign='left'
                                >
                                    {routingInfo?.destinationStation ?? '- -'}
                                </Grid>
                            </Grid>
                        </Grid>
                        {/* Pieces and Total Weight */}
                        <Grid item xs={12} className={`${belowSm ? 'centered' : ''}`}>
                            <Grid container className={`${belowSm ? 'count-bgfill' : ''} status-count`}>
                                <Grid item xs={5} className='status-left-count'>
                                    <Box>
                                        <Typography fontSize='12px'>Total Pieces</Typography>
                                        <Typography fontSize='16px'>{unitInfo?.totalPieces ?? '- -'}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2} />
                                <Grid item xs={5}>
                                    <Box>
                                        <Typography fontSize='12px'>Total Weight</Typography>
                                        <Typography fontSize='16px'>
                                            {unitInfo?.totalWeight
                                                ? `${unitInfo?.totalWeight} ${unitInfo?.weightUnit?.toLowerCase()}`
                                                : '- -'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    )
}

export default ShipmentStatus
